// src/Components/UserForm.js
import { memo, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../api/api.js';
import { makePersonSchema, getPasswordStrength } from '../lib/validations';
import Button from './ui/Button.jsx';
import PasswordInput from './ui/PasswordInput.jsx';
import InfoBox from './ui/InfoBox.jsx';
import { useToast } from './ui/Toast.jsx';
import f from '../styles/forms.module.css';

const blockNonLetters = (e) => {
  if (e.key.length === 1 && !/[a-zA-ZÀ-ÿ\s]/.test(e.key)) e.preventDefault();
};

const blockNonDigits = (e) => {
  const nav = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (!nav.includes(e.key) && !/^[0-9]$/.test(e.key)) e.preventDefault();
};

const ROLE_LABELS = { admin: 'Administrador', employee: 'Empleado', client: 'Cliente' };

const UserForm = memo(({ form, resetForm, onSuccess }) => {
  const isEditing = !!form.id;
  const { showToast } = useToast();
  const schema = useMemo(() => makePersonSchema(isEditing), [isEditing]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name:           form.name           || '',
      last_name:      form.last_name      || '',
      identification: form.identification || '',
      birthdate:      form.birthdate      || '',
      mail:           form.mail           || '',
      tel_number:     form.tel_number     || '',
      role:           form.role           || 'client',
      password:       '',
      oldPassword:    '',
    },
  });

  useEffect(() => {
    reset({
      name:           form.name           || '',
      last_name:      form.last_name      || '',
      identification: form.identification || '',
      birthdate:      form.birthdate      || '',
      mail:           form.mail           || '',
      tel_number:     form.tel_number     || '',
      role:           form.role           || 'client',
      password:       '',
      oldPassword:    '',
    });
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [serverError, setServerError] = useState('');

  const passwordValue = watch('password') || '';
  const strength      = getPasswordStrength(passwordValue);

  const onSubmit = async (data) => {
    setServerError('');
    try {
      // Cuando se edita, el email está deshabilitado y no viene en data → usar form.mail
      const email = isEditing ? form.mail : (data.mail || '').toLowerCase().trim();
      const payload = {
        Name:           data.name.trim(),
        Last_name:      data.last_name.trim(),
        Email:          email,
        Number_phone:   data.tel_number,
        Identification: data.identification.trim(),
        Birthdate:      data.birthdate,
        Rol:            data.role || form.role,
      };

      if (data.password) {
        payload.password = data.password;
        if (isEditing) payload.oldPassword = data.oldPassword;
      }

      if (isEditing) {
        await api.put(`/user/${form.id}`, payload);
        // Pasar campos normalizados que UserList entiende (soporta Name/name y Email/mail)
        onSuccess({
          id:           form.id,
          ID:           form.id,
          Name:         payload.Name,
          Last_name:    payload.Last_name,
          Email:        payload.Email,
          Number_phone: payload.Number_phone,
          Rol:          payload.Rol,
          Identification: payload.Identification,
          Birthdate:    payload.Birthdate,
        });
      } else {
        const res = await api.post('/user', payload);
        const userId = res.data.userId || res.data.id;
        if (!userId) throw new Error('El servidor no devolvió el ID del usuario creado');
        onSuccess({ id: userId, name: data.name, last_name: data.last_name, mail: data.mail, role: data.role });
      }

      showToast(`Usuario ${isEditing ? 'actualizado' : 'creado'} correctamente`, { type: 'success' });
      resetForm();
    } catch (err) {
      setServerError(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        err.message ||
        'Hubo un error al guardar el usuario.'
      );
    }
  };

  return (
    <div className={f.card}>
      {/* Header */}
      <div className={f.cardHeader}>
        <div className={f.cardTitleGroup}>
          <h3 className={f.cardTitle}>{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
          <p className={f.cardDesc}>
            {isEditing
              ? `Editando: ${form.name || ''} ${form.last_name || ''} — Rol: ${ROLE_LABELS[form.role] || form.role}`
              : 'Crea un nuevo usuario y asígnale un rol en el sistema.'}
          </p>
        </div>
        <span className={f.badgeAdmin}>Admin</span>
      </div>
      <hr className={f.divider} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {serverError && <div className={f.serverError} role="alert">{serverError}</div>}

        {/* ── Información Personal ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Información Personal</span>
          <div className={f.grid2}>
            <div className={f.field}>
              <label htmlFor="usr-name" className={`${f.label} ${f.required}`}>Nombre</label>
              <input
                id="usr-name"
                placeholder="Ej: Carlos"
                className={`${f.input} ${errors.name ? f.inputError : ''}`}
                aria-describedby={errors.name ? 'usr-name-error' : undefined}
                aria-invalid={!!errors.name}
                onKeyDown={blockNonLetters}
                {...register('name')}
              />
              {errors.name && <span id="usr-name-error" className={f.fieldError} role="alert">{errors.name.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="usr-lastname" className={`${f.label} ${f.required}`}>Apellido</label>
              <input
                id="usr-lastname"
                placeholder="Ej: Rodríguez"
                className={`${f.input} ${errors.last_name ? f.inputError : ''}`}
                aria-describedby={errors.last_name ? 'usr-lastname-error' : undefined}
                aria-invalid={!!errors.last_name}
                onKeyDown={blockNonLetters}
                {...register('last_name')}
              />
              {errors.last_name && <span id="usr-lastname-error" className={f.fieldError} role="alert">{errors.last_name.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="usr-id" className={`${f.label} ${f.required}`}>Identificación</label>
              <input
                id="usr-id"
                placeholder="Solo números"
                inputMode="numeric"
                className={`${f.input} ${errors.identification ? f.inputError : ''}`}
                aria-describedby={errors.identification ? 'usr-id-error' : undefined}
                aria-invalid={!!errors.identification}
                onKeyDown={blockNonDigits}
                {...register('identification')}
              />
              {errors.identification && <span id="usr-id-error" className={f.fieldError} role="alert">{errors.identification.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="usr-birth" className={`${f.label} ${f.required}`}>Fecha de Nacimiento</label>
              <input
                id="usr-birth"
                type="date"
                max={new Date().toISOString().split('T')[0]}
                className={`${f.input} ${errors.birthdate ? f.inputError : ''}`}
                aria-describedby={errors.birthdate ? 'usr-birth-error' : undefined}
                aria-invalid={!!errors.birthdate}
                {...register('birthdate')}
              />
              {errors.birthdate && <span id="usr-birth-error" className={f.fieldError} role="alert">{errors.birthdate.message}</span>}
            </div>
          </div>
        </div>

        {/* ── Contacto ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Contacto</span>
          <div className={f.grid2}>
            <div className={f.field}>
              <label htmlFor="usr-mail" className={`${f.label} ${f.required}`}>Correo Electrónico</label>
              <input
                id="usr-mail"
                type="email"
                placeholder="usuario@flamingo.com"
                className={`${f.input} ${errors.mail ? f.inputError : ''}`}
                aria-describedby={errors.mail ? 'usr-mail-error' : undefined}
                aria-invalid={!!errors.mail}
                disabled={isEditing}
                {...register('mail')}
              />
              {isEditing && <span className={f.fieldHint}>El correo no puede modificarse</span>}
              {errors.mail && !isEditing && <span id="usr-mail-error" className={f.fieldError} role="alert">{errors.mail.message}</span>}
            </div>

            <div className={f.field}>
              <label htmlFor="usr-phone" className={`${f.label} ${f.required}`}>Teléfono</label>
              <input
                id="usr-phone"
                placeholder="Solo dígitos"
                inputMode="numeric"
                className={`${f.input} ${errors.tel_number ? f.inputError : ''}`}
                aria-describedby={errors.tel_number ? 'usr-phone-error' : undefined}
                aria-invalid={!!errors.tel_number}
                onKeyDown={blockNonDigits}
                {...register('tel_number')}
              />
              {errors.tel_number && <span id="usr-phone-error" className={f.fieldError} role="alert">{errors.tel_number.message}</span>}
            </div>
          </div>
        </div>

        {/* ── Rol ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>Rol y Permisos</span>
          <div className={f.field} style={{ maxWidth: '260px' }}>
            <label htmlFor="usr-role" className={`${f.label} ${f.required}`}>Rol en el Sistema</label>
            <select
              id="usr-role"
              className={`${f.select} ${errors.role ? f.inputError : ''}`}
              aria-describedby={errors.role ? 'usr-role-error' : 'usr-role-hint'}
              aria-invalid={!!errors.role}
              {...register('role')}
            >
              <option value="client">Cliente</option>
              <option value="employee">Empleado</option>
              <option value="admin">Administrador</option>
            </select>
            {errors.role
              ? <span id="usr-role-error" className={f.fieldError} role="alert">{errors.role.message}</span>
              : <span id="usr-role-hint" className={f.fieldHint}>Define el nivel de acceso al sistema</span>
            }
          </div>
        </div>

        {/* ── Contraseña ── */}
        <div className={f.section}>
          <span className={f.sectionLabel}>{isEditing ? 'Cambio de Contraseña' : 'Acceso al Sistema'}</span>

          {isEditing && (
            <InfoBox>
              Deja estos campos vacíos si no necesitas cambiar la contraseña del usuario.
            </InfoBox>
          )}

          <div className={f.grid2} style={{ marginTop: isEditing ? '12px' : '0' }}>
            {isEditing && (
              <div className={f.field}>
                <label htmlFor="usr-old-pwd" className={f.label}>Contraseña Actual</label>
                <PasswordInput
                  id="usr-old-pwd"
                  placeholder="Contraseña actual"
                  error={!!errors.oldPassword}
                  aria-describedby={errors.oldPassword ? 'usr-old-pwd-error' : undefined}
                  {...register('oldPassword')}
                />
                {errors.oldPassword && <span id="usr-old-pwd-error" className={f.fieldError} role="alert">{errors.oldPassword.message}</span>}
              </div>
            )}

            <div className={f.field}>
              <label htmlFor="usr-pwd" className={`${f.label} ${!isEditing ? f.required : ''}`}>
                {isEditing ? 'Nueva Contraseña' : 'Contraseña'}
              </label>
              <PasswordInput
                id="usr-pwd"
                placeholder={isEditing ? 'Nueva contraseña (opcional)' : 'Mínimo 8 caracteres'}
                error={!!errors.password}
                showStrength
                strengthValue={strength}
                aria-describedby={errors.password ? 'usr-pwd-error' : undefined}
                {...register('password')}
              />
              {errors.password && <span id="usr-pwd-error" className={f.fieldError} role="alert">{errors.password.message}</span>}
            </div>
          </div>
        </div>

        {/* ── Acciones ── */}
        <div className={f.actions}>
          <Button type="submit" variant="primary" loading={isSubmitting}>
            {isEditing ? 'Actualizar Usuario' : 'Crear Usuario'}
          </Button>
          {isEditing && (
            <Button type="button" variant="secondary" onClick={resetForm}>
              Cancelar
            </Button>
          )}
        </div>
      </form>
    </div>
  );
});

export default UserForm;
